import {
    Component,
    EventEmitter,
    HostListener,
    Inject,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges
} from '@angular/core';
import {AuditActivityId, DeviceId, FindingId, TestRunId, VideoRecordingId} from '@webmate-web-core/shared/types/ids';
import {DeviceService} from '@webmate-web-core/shared/services/device/device.service';
import {Device} from '@webmate-web-core/shared/types/devices/device';
import {VideoRecordingService} from '@webmate-web-core/shared/services/video-recording.service';
import {mergeMap, take} from 'rxjs/operators';
import {faExternalLink} from '@fortawesome/pro-solid-svg-icons';
import { WorkbenchViewComponent } from '../../../../../shared/components/viewcontainers/workbench-view.component';
import {ViewId, WORKBENCH_VIEW_CONFIG, WorkbenchViewConfig} from '../../../../../shared/types';
import {WorkbenchContextService} from '../../../../../shared/services/workbench-context.service';
import {ConsoleService} from '../../../services/console.service';
import {WorkbenchCommandRegistry} from '../../../../../shared/services/workbench-command-registry.service';
import {WorkbenchLayoutManager} from '../../../../../shared/services/workbench-layout-manager.service';
import {DevicePropertyNames} from '@webmate-web-core/shared/types/devices/device-properties';
import {SelectFindingEvent} from '../../../../../shared/components/inspector/inspector-shared-tools/select-finding.component';
import {WorkbenchTestService} from '../../../../../shared/services/workbench-test.service';
import {FindingCellInsertService} from '@webmate-web-core/shared/services/finding-cell-insert.service';

@Component({
    selector: 'wm-take-video-snippet',
    templateUrl: './take-video-snippet.component.html',
    styleUrls: ['./take-video-snippet.component.less']
})
export class TakeVideoSnippetComponent extends WorkbenchViewComponent implements OnInit, OnChanges {

    @Input() consoleViewId: ViewId;

    @Input() deviceId: DeviceId;

    @Input() preselectedFinding?: FindingId;

    @Input() initRecording: VideoRecordingId;

    @Output() recordingStateChanged: EventEmitter<boolean> = new EventEmitter();

    device?: Device;

    activeTestRun?: TestRunId;
    activeActivity?: AuditActivityId;

    mediaElement?: HTMLCanvasElement | HTMLVideoElement;
    mediaRecorder?: MediaRecorder;
    recordedChunks: Array<any> = [];
    mimeType?: MimeType;
    captureFps: number = 25;
    blobTimeSlice: 1000; // 1 sec

    recordInProgress: boolean = false;
    validVideo: boolean = false;

    errorMsg: string = "";

    wantNativeVideoRecording: boolean = false;
    nativeVideoRecordingAvailable: boolean = false;
    nativeVideoRecordId?: VideoRecordingId;

    finishedVideoRecording: VideoRecordingId;

    recordVideoAnimationFrameId: any;

    automaticallyAddToFinding: boolean = false;
    selectedFinding?: FindingId;
    findingCount: number;

    constructor(private readonly deviceService: DeviceService,
                private readonly workbenchContextService: WorkbenchContextService,
                private readonly videoRecordingService: VideoRecordingService,
                private readonly consoleService: ConsoleService,
                private workbenchCommandRegistry: WorkbenchCommandRegistry,
                private workbenchTestService: WorkbenchTestService,
                private findingCellInsertService: FindingCellInsertService,
                layoutManager: WorkbenchLayoutManager,
                @Inject(WORKBENCH_VIEW_CONFIG) public config: WorkbenchViewConfig<any>) {
        super(layoutManager, config);
    }

    ngOnInit() {
        this.mimeType = getSupportedMimeType();
        if (!this.mimeType) {
            this.errorMsg = 'The feature is not supported in this browser!';
        }

        this.layoutManager.subscribeActiveViewId$().subscribe(activeView => {
            if (!this.wantNativeVideoRecording && this.recordInProgress && (activeView !== this.config.id)) {
                this.stopRecording();
            }
        })

        this.deviceService.getDevice$(this.deviceId).subscribe(device => {
            this.device = device;
            this.nativeVideoRecordingAvailable = this.device.properties[DevicePropertyNames.ProviderType] === 'OpenSTF';
            this.wantNativeVideoRecording = this.wantNativeVideoRecording && this.nativeVideoRecordingAvailable;
        });

        this.workbenchContextService.subscribeWorkbenchContext$().subscribe(context => {
            this.activeTestRun = context?.testRunContext?.activeTestRun;
            this.activeActivity = context?.auditContext?.activeAuditActivity?.id;
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        // The running native recording might be loaded after initialization of the component,
        // so check in onChanges for a new value
        const change = changes['initRecording'];
        if (change && change.previousValue === undefined && change.currentValue !== undefined) {
            this.nativeVideoRecordId = this.initRecording;
            this.wantNativeVideoRecording = true;
            this.recordInProgress = true;
        }
    }

    @HostListener('document:visibilitychange', ['$event'])
    tabHidden() {
        if (!this.wantNativeVideoRecording && this.recordInProgress && document.visibilityState === 'hidden') {
            this.stopRecording();
        }
    }

    startRecording() {
        this.finishedVideoRecording = undefined;
        if (this.wantNativeVideoRecording && this.nativeVideoRecordingAvailable) {
            this.startNativeRecording();
        } else {
            this.startLiveRecording();
        }
    }

    stopRecording() {
        if (this.nativeVideoRecordingAvailable && this.nativeVideoRecordId) {
            this.stopNativeRecording();
        } else {
            this.stopLiveRecording();
        }
        this.recordingStateChanged.emit(false);

    }

    getMediaElement(): HTMLCanvasElement | HTMLVideoElement | undefined {
        if (!this.consoleViewId) {
            console.error("No active console");
            return undefined;
        }
        const consoleDomElem = this.layoutManager.getViewDomElement(this.consoleViewId);
        if (!consoleDomElem) {
            console.error("No console dom element found for active console");
            return undefined;
        }
        const canvases = $(consoleDomElem)?.find("canvas");
        const deviceCanvas = canvases.not("#vision-sim-overlay-canvas")?.[0] as HTMLCanvasElement;
        const simulationCanvas = canvases.filter("#vision-sim-overlay-canvas")?.[0] as HTMLCanvasElement;
        // the simulation canvas must be chosen for live-recording if active
        if (simulationCanvas?.classList.contains("vision-sim-active")) {
            return simulationCanvas;
        }
        if (deviceCanvas) {
            return deviceCanvas;
        }
        // Headless consoles render a <video> instead of a <canvas>.
        const video = $(consoleDomElem)?.find("video")?.[0] as HTMLVideoElement | undefined;
        if (!video) {
            console.error("No canvas or video element found for active console");
        }
        return video;
    }

    getCanvasRotation(source: HTMLCanvasElement | HTMLVideoElement): number {
        const transform = source?.style.transform || '';
        if (!transform.includes('rotate')) {
            return 0;
        }
        const matches = transform.match(/rotate\(([+-]?\d+)deg\)/);
        return matches ? parseFloat(matches[1]) : 0;
    }

    updateRecordCanvas(recordCanvas: HTMLCanvasElement, source: HTMLCanvasElement | HTMLVideoElement) {
        const rotation = this.getCanvasRotation(source);
        // A <video> exposes its intrinsic frame size via videoWidth/videoHeight, not width/height.
        const sourceWidth = source instanceof HTMLVideoElement ? source.videoWidth : source.width;
        const sourceHeight = source instanceof HTMLVideoElement ? source.videoHeight : source.height;

        if (rotation % 180 === 0) {
            recordCanvas.width = sourceWidth;
            recordCanvas.height = sourceHeight;
        } else {
            recordCanvas.width = sourceHeight;
            recordCanvas.height = sourceWidth;
        }

        const ctx = recordCanvas.getContext("2d");
        if (!ctx) return;

        ctx.save();
        ctx.clearRect(0, 0, recordCanvas.width, recordCanvas.height);
        ctx.translate(recordCanvas.width / 2, recordCanvas.height / 2);
        ctx.rotate((rotation * Math.PI) / 180);
        ctx.drawImage(source, -sourceWidth / 2, -sourceHeight / 2);
        ctx.restore();

        if (this.recordInProgress) {
            this.recordVideoAnimationFrameId = requestAnimationFrame(() =>
                this.updateRecordCanvas(recordCanvas, source)
            );
        }
    }

    startNativeRecording = () => {
        if (this.device) {
            this.consoleService.startVideoRecording(this.device!.id).subscribe((rid) => {
                this.nativeVideoRecordId = rid;
                this.recordInProgress = true;
                this.recordingStateChanged.emit(true);
            });
        }
    }

    stopNativeRecording = () => {
        if (this.nativeVideoRecordId) {
            this.consoleService.stopVideoRecording(this.nativeVideoRecordId!).subscribe(_ => {
                this.finishedVideoRecording = this.nativeVideoRecordId;
                this.recordInProgress = false;
                this.nativeVideoRecordId = undefined;
                if (this.automaticallyAddToFinding) {
                    this.addToFinding();
                }
            });
        }
    }

    startLiveRecording = () => {
        if (!this.mimeType) {
            return;
        }
        this.recordedChunks = [];
        this.recordInProgress = false;
        this.errorMsg = "";
        if (!this.mediaElement) {
            const media = this.getMediaElement();
            if (!media) {
                this.errorMsg = "Couldn't find the console";
                return;
            }
            this.mediaElement = media;
        }
        if (!this.mediaElement) {
            this.errorMsg = "Couldn't find the console";
            return;
        }
        const recordCanvas = document.createElement("canvas");
        this.recordVideoAnimationFrameId = requestAnimationFrame(() => this.updateRecordCanvas(recordCanvas, this.mediaElement!));

        this.recordInProgress = true;
        this.recordingStateChanged.emit(true);
        this.recordedChunks = [];

        const stream = recordCanvas.captureStream(this.captureFps);
        this.mediaRecorder = new MediaRecorder(stream, {mimeType: this.mimeType.mime});

        this.mediaRecorder.ondataavailable = this.onDataAvailableHandler;
        this.mediaRecorder.onstop = this.storeCapturedVideoFromBrowser;
        this.mediaRecorder.onerror = (e) => console.error(e);
        this.mediaRecorder.start(this.blobTimeSlice);
    }

    stopLiveRecording(){
        this.mediaRecorder?.stop();
    }

    storeCapturedVideoFromBrowser = () => {
        const blob = this.stitchVideoFromChunks();
        if (this.device && this.validVideo && blob)  {
            this.videoRecordingService.storeVideo(this.workbenchContextService.getProjectId(), blob, 'Video of ' + this.getConsoleDisplayName(), this.device.id).subscribe(videoId => {
                this.finishedVideoRecording = videoId;
                if (this.automaticallyAddToFinding) {
                    this.addToFinding();
                }
            });
        }
    }

    onDataAvailableHandler = (event: any) => {
        if (event.data.size > 0) {
            this.recordedChunks.push(event.data);
        }
    };

    stitchVideoFromChunks() {
        let videoBlob: Blob | undefined;
        if (this.recordedChunks.length > 0) {
            videoBlob = new Blob(this.recordedChunks, {
                type: this.mimeType!.mime
            });
            this.recordedChunks = [];
            this.validVideo = true;
        } else {
            this.validVideo = false;
            this.errorMsg = "Nothing happened in console to record";
        }
        this.recordInProgress = false;
        return videoBlob;
    };

    openVideoInViewer() {
        this.videoRecordingService.getVideoRecording(this.finishedVideoRecording).pipe(take(1)).subscribe(videoRecording => {
            this.workbenchCommandRegistry.executeCommand('workbench-tools:openVideo', {
                id: videoRecording.id,
                name: videoRecording.metadata?.name ?? 'Unnamed Video',
                duration: videoRecording.metadata?.duration,
                size: videoRecording.estimatedSize,
                format: videoRecording.fileFormat
            });
        })
    }

    handleFindingStateChange(event: SelectFindingEvent) {
        this.automaticallyAddToFinding = event.addAutomatically;
        this.selectedFinding = event.findingId;
        this.findingCount = event.findingCount;
    }

    addToFinding() {
        if (this.selectedFinding) {
            this.findingCellInsertService.clearPendingInsertsExcept(this.selectedFinding);
            this.workbenchTestService.addVideoToFinding(this.selectedFinding, this.finishedVideoRecording).subscribe()
        } else {
            this.findingCellInsertService.clearPendingInsertsExcept();
            this.workbenchTestService.createNewManualFinding(`Finding #${this.findingCount + 1}`).pipe(mergeMap(findingId => {
                return this.workbenchTestService.addVideoToFinding(findingId, this.finishedVideoRecording);
            })).subscribe();
        }
    }

    /** Default label for captured videos: the console tab's name — the browser session for a headless
     *  console, the device name for other consoles — falling back to the device name. */
    private getConsoleDisplayName(): string {
        return this.layoutManager.getViewConfig(this.consoleViewId)?.name || this.device?.name || 'device';
    }

    protected readonly faExternalLink = faExternalLink;
}

type MimeType = {
  mime: string;
  extension: string;
};

function getSupportedMimeType(): MimeType | undefined {
    const ua = navigator.userAgent.toLowerCase();

    if (ua.includes('firefox') || ua.includes('edg')) {
        // Firefox and Edge -> use webm
        return { mime: "video/webm", extension: "webm" };
    } else {
        // Safari, Chrome, others -> use mp4
        return { mime: "video/mp4", extension: "mp4" };
    }
}
